import React, { useMemo, useState } from "react";

// Presentational organization auth branding. It renders the organization's own
// identity (logo + name, org color) on donor/member auth screens. It is pure:
// callers pass already-resolved branding props; no Firestore reads or
// org-resolution logic live here (keep that in a separate resolver hook).
//
// Guarantees:
//  - Never shows "Lightly Technologies" or any platform branding.
//  - Never shows a broken image icon: a missing/failed logo falls back to a
//    clean initials avatar rendered as an inline SVG data URL (mirroring the
//    manifest/cartoonAvatar initials pattern), which cannot 404.

const NEUTRAL_BRAND_COLOR = "#1F2937";

function isHexColor(value) {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || "").trim());
}

function escapeXml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Tenant-agnostic initials from an org name (mirrors buildOrgManifest's logic).
export function deriveOrgInitials(name) {
  const cleaned = String(name || "")
    .replace(/\b(the|of|and|for|church|ministries|ministry|organization|inc|llc)\b/gi, " ")
    .replace(/[^A-Za-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = cleaned.split(" ").filter(Boolean);
  const initials = words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
  return initials || "O";
}

// Inline SVG data URL initials avatar (mirrors generatedInitialsIcon /
// cartoonAvatarDataUrl): org color background, white initials. Cannot 404.
export function orgInitialsDataUrl({ initials, color }) {
  const bg = isHexColor(color) ? color : NEUTRAL_BRAND_COLOR;
  const text = escapeXml((initials || "O").slice(0, 3));
  const fontSize = text.length > 2 ? 150 : 188;
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">',
    `<rect width="512" height="512" rx="112" fill="${bg}"/>`,
    `<text x="256" y="282" text-anchor="middle" dominant-baseline="middle" font-family="Inter, Arial, sans-serif" font-size="${fontSize}" font-weight="800" fill="#ffffff">${text}</text>`,
    "</svg>",
  ].join("");
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export default function OrgAuthBrand({
  name,
  logoUrl,
  primaryColor,
  shortName,
  initials,
  size = 72,
  showName = true,
  className = "",
}) {
  const [logoFailed, setLogoFailed] = useState(false);

  const displayName = String(name || shortName || "").trim();
  const resolvedInitials = useMemo(
    () => (String(initials || "").trim() || deriveOrgInitials(shortName || name)),
    [initials, shortName, name]
  );
  const initialsSrc = useMemo(
    () => orgInitialsDataUrl({ initials: resolvedInitials, color: primaryColor }),
    [resolvedInitials, primaryColor]
  );

  const cleanLogo = String(logoUrl || "").trim();
  const showLogo = Boolean(cleanLogo) && !logoFailed;
  const dimension = Number.isFinite(Number(size)) ? Number(size) : 72;

  return (
    <div className={`flex flex-col items-center text-center ${className}`}>
      <img
        src={showLogo ? cleanLogo : initialsSrc}
        alt={displayName ? `${displayName} logo` : "Organization logo"}
        width={dimension}
        height={dimension}
        decoding="async"
        loading="eager"
        draggable={false}
        onError={showLogo ? () => setLogoFailed(true) : undefined}
        style={{
          width: dimension,
          height: dimension,
          objectFit: "contain",
          borderRadius: 18,
          display: "block",
        }}
      />
      {showName && displayName ? (
        <div
          className="mt-3 text-lg font-bold"
          style={{ color: isHexColor(primaryColor) ? primaryColor : "#0b1220" }}
        >
          {displayName}
        </div>
      ) : null}
    </div>
  );
}
